import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, InjectionToken, PLATFORM_ID, DOCUMENT } from '@angular/core';
import each from 'lodash-es/each';

export interface ICookiesData {
    [cookieName: string]: string;
}

@Injectable({
    providedIn: 'root'
})
export class CookiesService {
    protected readonly documentIsAccessible: boolean;

    constructor(
        @Inject(DOCUMENT) protected document: Document,
        @Inject(PLATFORM_ID)
        protected platformId: InjectionToken<{ [key: string]: any }>
    ) {
        this.documentIsAccessible = isPlatformBrowser(this.platformId);
    }

    /**
     * check if cookie already exist
     * @param name Cookie name
     * @returns {boolean}
     */
    check(name: string): boolean {
        if (!this.documentIsAccessible) {
            return false;
        }

        name = encodeURIComponent(name);

        const regExp: RegExp = this.getCookieRegExp(name);
        const exists: boolean = regExp.test(this.document.cookie);

        return exists;
    }

    /**
     * if cookie exist get it
     * @param name Cookie name
     * @returns {string}
     */
    get(name: string): string {
        if (this.documentIsAccessible && this.check(name)) {
            name = encodeURIComponent(name);

            const regExp: RegExp = this.getCookieRegExp(name);
            const result: RegExpExecArray | null = regExp.exec(this.document.cookie);

            if (result) {
                return decodeURIComponent(result[1]);
            }
        }
        return '';
    }

    /**
     * get all cookies
     * @returns {[cookieName : string] : string}
     */
    getAll(): ICookiesData {
        if (!this.documentIsAccessible) {
            return {};
        }

        const cookies: ICookiesData = {};
        const document: Document = this.document;

        if (document.cookie && document.cookie !== '') {
            const split: string[] = document.cookie.split(';');

            for (const part of split) {
                const currentCookie: string[] = part.split('=');
                currentCookie[0] = currentCookie[0].replace(/^ /, '');
                cookies[decodeURIComponent(currentCookie[0])] = decodeURIComponent(currentCookie[1]);
            }
        }

        return cookies;
    }

    /**
     * set cookie
     * @param name     Cookie name
     * @param value    Cookie value
     * @param expires  Number of days until the cookies expires or an actual `Date`
     * @param path     Cookie path
     * @param domain   Cookie domain
     * @param secure   Secure flag
     * @param sameSite OWASP samesite token `Lax` or `Strict`
     */
    set(
        name: string,
        value: string,
        expires?: number | Date,
        path?: string,
        domain?: string,
        sameSite?: 'Lax' | 'Strict',
        secure: boolean = true
    ): void {
        if (!this.documentIsAccessible) {
            return;
        }

        let cookieString: string = encodeURIComponent(name) + '=' + encodeURIComponent(value) + ';';

        if (!expires) {
            expires = 365;
        }

        let expiresDate: Date;

        if (typeof expires === 'number') {
            expiresDate = new Date();
            expiresDate.setDate(expiresDate.getDate() + expires);
        } else {
            expiresDate = expires;
        }
        cookieString += 'expires=' + expiresDate.toUTCString() + ';';

        cookieString += 'path=' + (path || '/') + ';';

        if (domain) {
            cookieString += 'domain=' + domain + ';';
        }

        if (secure) {
            cookieString += 'secure;';
        }

        if (sameSite) {
            cookieString += 'sameSite=' + sameSite + ';';
        }

        this.document.cookie = cookieString;
    }

    /**
     * delete cookie
     * @param name   Cookie name
     * @param path   Cookie path
     * @param domain Cookie domain
     */
    delete(name: string, path?: string, domain?: string): void {
        if (!this.documentIsAccessible) {
            return;
        }

        if (domain === undefined) {
            const _domainParts = window.location.host.split('.');
            _domainParts
                .map((v, index) => _domainParts.slice(index).join('.'))
                .forEach(domainPart => {
                    this.delete(name, '', domainPart);
                });
        }

        this.set(name, '', new Date('Thu, 01 Jan 1970 00:00:01 GMT'), path, domain);
    }

    /**
     * delete all cookies
     * @param path   Cookie path
     * @param domain Cookie domain
     */
    deleteAll(path?: string, domain?: string): void {
        if (!this.documentIsAccessible) {
            return;
        }
        each(this.getAll(), cookieName => this.delete(cookieName, path, domain));
    }

    /**
     * @param name Cookie name
     * @returns {RegExp}
     */
    protected getCookieRegExp(name: string): RegExp {
        const escapedName: string = name.replace(/([\[\]\{\}\(\)\|\=\;\+\?\,\.\*\^\$])/gi, '\\$1');

        return new RegExp('(?:^' + escapedName + '|;\\s*' + escapedName + ')=(.*?)(?:;|$)', 'g');
    }
}
